import { defaultDecimalSeparator } from './format'

export type OptionalNumber = number | null | undefined

export type OptionalSize = readonly [OptionalNumber, OptionalNumber]

export type Size = readonly [number, number]

interface SizeStringOptions {
  valueSeparator?: string
  spacingAroundSeparator?: boolean
  decimalSeparator?: string
}

const passepartoutWidthReduction = 1
const passepartoutHeightReduction = 1

/**
 * Determines whether size is larger than or equal to minimum size.
 */
export const getIsLargerThan = (
  size: OptionalSize,
  minimumSize: OptionalSize,
) => {
  const width = size[0] ?? undefined
  const height = size[1] ?? undefined
  const minimumWidth = minimumSize[0] ?? undefined
  const minimumHeight = minimumSize[1] ?? undefined

  if (typeof width === 'undefined' || typeof height === 'undefined') {
    return true
  }

  // Compare 1 dimension
  if (
    typeof minimumWidth !== 'undefined' &&
    typeof minimumHeight === 'undefined'
  ) {
    return width >= minimumWidth || height >= minimumWidth
  }

  if (
    typeof minimumWidth === 'undefined' &&
    typeof minimumHeight !== 'undefined'
  ) {
    return width >= minimumHeight || height >= minimumHeight
  }

  // Compare 2 dimensions
  if (
    typeof minimumWidth !== 'undefined' &&
    typeof minimumHeight !== 'undefined'
  ) {
    return (
      (width >= minimumWidth && height >= minimumHeight) ||
      (height >= minimumWidth && width >= minimumHeight)
    )
  }

  return true
}

/**
 * Determines whether size is smaller than or equal to maximum size.
 */
export const getIsSmallerThan = (
  size: OptionalSize,
  maximumSize: OptionalSize,
) => {
  const width = size[0] ?? undefined
  const height = size[1] ?? undefined
  const maximumWidth = maximumSize[0] ?? undefined
  const maximumHeight = maximumSize[1] ?? undefined

  if (typeof width === 'undefined' || typeof height === 'undefined') {
    return true
  }

  // Compare 1 dimension
  if (
    typeof maximumWidth !== 'undefined' &&
    typeof maximumHeight === 'undefined'
  ) {
    return width <= maximumWidth || height <= maximumWidth
  }

  if (
    typeof maximumWidth === 'undefined' &&
    typeof maximumHeight !== 'undefined'
  ) {
    return width <= maximumHeight || height <= maximumHeight
  }

  // Compare 2 dimensions
  if (
    typeof maximumWidth !== 'undefined' &&
    typeof maximumHeight !== 'undefined'
  ) {
    return (
      (width <= maximumWidth && height <= maximumHeight) ||
      (height <= maximumWidth && width <= maximumHeight)
    )
  }

  return true
}

/**
 * Determines whether size is larger than or equal to minimum size and smaller than or equal to maximum size.
 */
export const getIsSizeWithinBounds = (
  size: OptionalSize,
  minimumSize: OptionalSize,
  maximumSize: OptionalSize,
) => {
  return (
    getIsLargerThan(size, minimumSize) && getIsSmallerThan(size, maximumSize)
  )
}

/**
 * Gets width and height from given string.
 */
export const getSizeDimensionsFromString = (
  sizeString: string,
  separator = 'x',
) => {
  const [width, height] = sizeString
    .replaceAll(' ', '')
    .replaceAll(',', '.')
    .split(separator)

  return [parseFloat(width), parseFloat(height)] as const
}

/**
 * Gets an array with frame's total width and height.
 */
export const getFrameSize = (
  artworkWidth: number,
  artworkHeight: number,
  passepartoutTopSize: number,
  passepartoutRightSize: number,
  passepartoutBottomSize: number,
  passepartoutLeftSize: number,
) => {
  if (
    passepartoutTopSize === 0 &&
    passepartoutRightSize === 0 &&
    passepartoutBottomSize === 0 &&
    passepartoutLeftSize === 0
  ) {
    const normalizedArtworkWidth = parseFloat(artworkWidth.toFixed(1))
    const normalizedArtworkHeight = parseFloat(artworkHeight.toFixed(1))

    return [normalizedArtworkWidth, normalizedArtworkHeight] as const
  }

  const frameWidth =
    artworkWidth -
    passepartoutWidthReduction +
    passepartoutLeftSize +
    passepartoutRightSize
  const frameHeight =
    artworkHeight -
    passepartoutHeightReduction +
    passepartoutTopSize +
    passepartoutBottomSize

  const normalizedFrameWidth = parseFloat(frameWidth.toFixed(1))
  const normalizedFrameHeight = parseFloat(frameHeight.toFixed(1))

  return [normalizedFrameWidth, normalizedFrameHeight] as const
}

/**
 * Gets passepartout hole size from artwork size.
 */
export const getPassepartoutHoleSizeFromArtworkSize = (
  artworkWidth: number,
  artworkHeight: number,
) => {
  const passepartoutHoleWidth = artworkWidth - passepartoutWidthReduction
  const passepartoutHoleHeight = artworkHeight - passepartoutHeightReduction

  const normalizedPassepartoutHoleWidth = parseFloat(
    passepartoutHoleWidth.toFixed(1),
  )
  const normalizedPassepartoutHoleHeight = parseFloat(
    passepartoutHoleHeight.toFixed(1),
  )

  return [
    normalizedPassepartoutHoleWidth,
    normalizedPassepartoutHoleHeight,
  ] as const
}

/**
 * Gets artwork size from passepartout hole size.
 */
export const getArtworkSizeFromPassepartoutHoleSize = (
  passepartoutHoleWidth: number,
  passepartoutHoleHeight: number,
) => {
  const artworkWidth = passepartoutHoleWidth + passepartoutWidthReduction
  const artworkHeight = passepartoutHoleHeight + passepartoutHeightReduction

  const normalizedArtworkWidth = parseFloat(artworkWidth.toFixed(1))
  const normalizedArtworkHeight = parseFloat(artworkHeight.toFixed(1))

  return [normalizedArtworkWidth, normalizedArtworkHeight] as const
}

/**
 * Converts width and height into a string.
 */
export const getSizeString = (
  width: number,
  height: number,
  options?: SizeStringOptions,
) => {
  const valueSeparator = options?.valueSeparator ?? 'x'
  const spacer = options?.spacingAroundSeparator ? ' ' : ''
  const decimalSeparator = options?.decimalSeparator ?? defaultDecimalSeparator

  const normalizedWidth = parseFloat(width.toFixed(1))
  const normalizedHeight = parseFloat(height.toFixed(1))

  // Update decimal separators
  const widthParts = `${normalizedWidth}`.split('.')
  const heightParts = `${normalizedHeight}`.split('.')
  const widthString = `${widthParts.join(decimalSeparator)}`
  const heightString = `${heightParts.join(decimalSeparator)}`

  return `${widthString}${spacer}${valueSeparator}${spacer}${heightString}`
}
